import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Type, Image as ImageIcon, Trash2, ArrowUp, ArrowDown, Download, Save, Loader2, Layers as LayersIcon, Scissors } from "lucide-react";
import { api } from "@/lib/api";
import V5MaskEditor from "./V5MaskEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ASPECTS = { "9:16": [1080, 1920], "1:1": [1080, 1080], "4:5": [1080, 1350], "16:9": [1920, 1080], "4:3": [1440, 1080], "3:4": [1080, 1440] };
const FONTS = ["Inter", "Georgia", "Impact", "Arial Black", "Courier New", "Times New Roman", "Verdana"];
const uid = () => Math.random().toString(36).slice(2, 9);
const abs = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);

const defaultState = () => ({
  canvas: { aspect: "4:5" },
  background: { type: "solid", color: "#0f1115", color2: "#3b0764", angle: 90, path: null },
  layers: [],
  rendererVersion: "5.0.0",
});

const imgCache = {};
function loadImage(path) {
  return new Promise((resolve) => {
    if (imgCache[path]) return resolve(imgCache[path]);
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => { imgCache[path] = im; resolve(im); };
    im.onerror = () => resolve(null);
    im.src = abs(path);
  });
}

// Pure deterministic renderer — used identically by preview AND export.
function renderProject(ctx, W, H, state) {
  ctx.clearRect(0, 0, W, H);
  const bg = state.background || {};
  if (bg.type === "solid") { ctx.fillStyle = bg.color || "#000"; ctx.fillRect(0, 0, W, H); }
  else if (bg.type === "gradient") {
    const a = ((bg.angle || 90) * Math.PI) / 180;
    const g = ctx.createLinearGradient(W / 2 - Math.cos(a) * W / 2, H / 2 - Math.sin(a) * H / 2, W / 2 + Math.cos(a) * W / 2, H / 2 + Math.sin(a) * H / 2);
    g.addColorStop(0, bg.color || "#000"); g.addColorStop(1, bg.color2 || "#333");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  } else if (bg.type === "paper") {
    ctx.fillStyle = "#efe7d3"; ctx.fillRect(0, 0, W, H);
    const rg = ctx.createRadialGradient(W / 2, H / 2, H * 0.2, W / 2, H / 2, H * 0.75);
    rg.addColorStop(0, "rgba(0,0,0,0)"); rg.addColorStop(1, "rgba(90,70,40,0.28)");
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
  } else if (bg.type === "image" && bg._img) {
    drawCover(ctx, bg._img, 0, 0, W, H);
  } // transparent → nothing

  for (const l of state.layers) {
    ctx.save();
    ctx.globalAlpha = l.opacity ?? 1;
    if (l.type === "image" && (l._maskedImg || l._img)) {
      const im = l._maskedImg || l._img;
      const iw = im.width, ih = im.height;
      const scale = (l.scale ?? 1) * Math.min(W / iw, H / ih);
      const dw = iw * scale, dh = ih * scale;
      const cx = (l.x ?? 0.5) * W, cy = (l.y ?? 0.5) * H;
      ctx.translate(cx, cy); ctx.rotate(((l.rotation || 0) * Math.PI) / 180);
      const f = l.effects || {};
      ctx.filter = `grayscale(${f.grayscale || 0}) sepia(${f.sepia || 0}) brightness(${f.brightness ?? 1}) contrast(${f.contrast ?? 1}) saturate(${f.saturate ?? 1}) blur(${(f.blur || 0)}px)`;
      ctx.drawImage(im, -dw / 2, -dh / 2, dw, dh);
      ctx.filter = "none";
    } else if (l.type === "text") {
      const size = (l.size ?? 0.08) * H;
      const weight = l.weight || 700;
      ctx.font = `${weight} ${size}px ${l.font || "Inter"}, sans-serif`;
      ctx.textAlign = l.align || "center";
      ctx.textBaseline = "middle";
      const text = (l.uppercase ? (l.text || "").toUpperCase() : (l.text || ""));
      const lines = text.split("\n");
      const lh = size * (l.lineHeight ?? 1.15);
      const cx = (l.x ?? 0.5) * W, cy = (l.y ?? 0.5) * H;
      ctx.translate(cx, cy); ctx.rotate(((l.rotation || 0) * Math.PI) / 180);
      lines.forEach((ln, i) => {
        const y = (i - (lines.length - 1) / 2) * lh;
        if (l.shadow) { ctx.shadowColor = l.shadowColor || "rgba(0,0,0,0.6)"; ctx.shadowBlur = (l.shadowBlur ?? 8); }
        if (l.outline && (l.outlineWidth ?? 0) > 0) {
          ctx.lineWidth = (l.outlineWidth ?? 4); ctx.strokeStyle = l.outlineColor || "#000";
          ctx.lineJoin = "round"; ctx.strokeText(ln, 0, y);
        }
        ctx.shadowBlur = l.shadow ? (l.shadowBlur ?? 8) : 0;
        ctx.fillStyle = l.color || "#fff";
        ctx.fillText(ln, 0, y);
        ctx.shadowBlur = 0;
      });
    }
    ctx.restore();
  }
}
function drawCover(ctx, im, x, y, w, h) {
  const r = Math.max(w / im.width, h / im.height);
  const dw = im.width * r, dh = im.height * r;
  ctx.drawImage(im, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

export default function InstagramPostMakerV5() {
  const [state, setState] = useState(defaultState());
  const [sel, setSel] = useState(null);
  const [projId, setProjId] = useState(null);
  const [name, setName] = useState("Untitled V5 project");
  const [busy, setBusy] = useState("");
  const [ready, setReady] = useState(false);
  const [maskEdit, setMaskEdit] = useState(null); // layer id being masked
  const [liveRenderer, setLiveRenderer] = useState(null); // {fn, version, label}
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const [W, H] = ASPECTS[state.canvas.aspect] || ASPECTS["4:5"];

  // Active renderer: hot-swappable admin code (Phase 4) or built-in default.
  const activeRender = useCallback((ctx, w, h, st) => {
    if (liveRenderer?.fn) { try { return liveRenderer.fn(ctx, w, h, st); } catch (e) { /* fall through to built-in */ } }
    return renderProject(ctx, w, h, st);
  }, [liveRenderer]);

  // resolve image references then draw
  const draw = useCallback(async () => {
    const cvs = canvasRef.current; if (!cvs) return;
    const bg = state.background;
    if (bg.type === "image" && bg.path) bg._img = await loadImage(bg.path);
    for (const l of state.layers) {
      if (l.type === "image" && l.path) l._img = await loadImage(l.path);
      if (l.type === "image" && l.maskedPath) l._maskedImg = await loadImage(l.maskedPath);
      else if (l.type === "image") l._maskedImg = null;
    }
    const ctx = cvs.getContext("2d");
    activeRender(ctx, W, H, state);
  }, [state, W, H, activeRender]);
  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    api.v5Meta().then(() => setReady(true)).catch(() => setReady(true));
    api.v5RendererLive().then((r) => {
      if (r?.code) {
        try {
          // eslint-disable-next-line no-new-func
          const fn = new Function("ctx", "W", "H", "state", `${r.code}\n;return renderProject(ctx,W,H,state);`);
          setLiveRenderer({ fn, version: r.version, label: r.label });
        } catch (e) { /* keep built-in */ }
      }
    }).catch(() => {});
    const onPaste = async (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
      if (item) { const f = item.getAsFile(); if (f) await addImage(f, "subject"); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []); // eslint-disable-line

  const patch = (up) => setState((s) => ({ ...s, ...up }));
  const patchLayer = (id, up) => setState((s) => ({ ...s, layers: s.layers.map((l) => (l.id === id ? { ...l, ...up } : l)) }));
  const selLayer = state.layers.find((l) => l.id === sel);

  const addImage = async (file, role) => {
    setBusy("upload");
    try {
      const r = await api.igUpload(file, role === "subject" ? "main" : "ref");
      const layer = { id: uid(), type: "image", role, path: r.path, x: 0.5, y: 0.5, scale: 1, rotation: 0, opacity: 1, effects: {} };
      setState((s) => ({ ...s, layers: [...s.layers, layer] }));
      setSel(layer.id);
      toast.success(role === "subject" ? "Main subject added (original kept unchanged)" : "Image added");
    } catch { toast.error("Upload failed — use PNG/JPG/WebP"); }
    setBusy("");
  };
  const addText = (behind) => {
    const layer = { id: uid(), type: "text", text: "HEADLINE", font: "Impact", size: 0.1, weight: 800, color: "#ffffff", align: "center", x: 0.5, y: 0.4, rotation: 0, opacity: 1, uppercase: true, outline: true, outlineWidth: 6, outlineColor: "#000000", shadow: true, shadowBlur: 10, shadowColor: "rgba(0,0,0,0.55)", lineHeight: 1.1 };
    // "behind subject": insert before the first subject image layer
    setState((s) => {
      const idx = s.layers.findIndex((l) => l.type === "image" && l.role === "subject");
      const layers = [...s.layers];
      if (behind && idx >= 0) layers.splice(idx, 0, layer); else layers.push(layer);
      return { ...s, layers };
    });
    setSel(layer.id);
  };
  const move = (id, dir) => setState((s) => {
    const i = s.layers.findIndex((l) => l.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= s.layers.length) return s;
    const layers = [...s.layers]; [layers[i], layers[j]] = [layers[j], layers[i]];
    return { ...s, layers };
  });
  const del = (id) => { setState((s) => ({ ...s, layers: s.layers.filter((l) => l.id !== id) })); if (sel === id) setSel(null); };

  // click-drag to move selected layer on canvas
  const onCanvasDown = (e) => {
    if (!selLayer) return;
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { id: sel, rect };
  };
  const onCanvasMove = (e) => {
    if (!dragRef.current) return;
    const { rect } = dragRef.current;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    patchLayer(dragRef.current.id, { x, y });
  };
  const onCanvasUp = () => { dragRef.current = null; };

  const cleanState = (s) => ({ ...s, background: { ...s.background, _img: undefined }, layers: s.layers.map(({ _img, _maskedImg, ...l }) => l) });
  const save = async () => {
    setBusy("save");
    try { const r = await api.v5Save({ id: projId, name, state: cleanState(state) }); setProjId(r.id); toast.success("Project saved (no AI used)"); }
    catch { toast.error("Save failed"); }
    setBusy("");
  };
  const projectHash = () => { try { return btoa(unescape(encodeURIComponent(JSON.stringify(cleanState(state))))).slice(0, 24); } catch { return ""; } };
  const exportImg = async (fmt) => {
    setBusy("export");
    try {
      const off = document.createElement("canvas"); off.width = W; off.height = H;
      activeRender(off.getContext("2d"), W, H, state);
      const mime = fmt === "jpeg" ? "image/jpeg" : fmt === "webp" ? "image/webp" : "image/png";
      const dataUrl = off.toDataURL(mime, 0.95);
      // download
      const a = document.createElement("a"); a.href = dataUrl; a.download = `${name.replace(/\s+/g, "_")}.${fmt}`; a.click();
      // persist server-side (deterministic output; still no AI)
      await api.v5Export({ data_url: dataUrl, format: fmt, project_hash: projectHash(), renderer_version: state.rendererVersion, width: W, height: H });
      toast.success(`Exported ${fmt.toUpperCase()} · renderer ${state.rendererVersion}`);
    } catch (e) { toast.error("Export failed"); }
    setBusy("");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="ig-post-maker-v5">
      <div className="mx-auto max-w-[1600px] px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-black">Instagram Post Maker <span className="rounded bg-[#00F0FF]/15 px-2 py-0.5 text-sm text-[#00F0FF]">V5 · code compositor</span></h1>
            <p className="text-sm text-[#A1A1AA]">Deterministic — your uploaded subject is kept pixel-for-pixel. No AI, no credits. Renderer v{state.rendererVersion}.</p>
          </div>
          <div className="flex items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="v5-name" className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm" />
            <button onClick={save} disabled={busy === "save"} data-testid="v5-save" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/15"><Save size={15} /> Save</button>
            <button onClick={() => exportImg("png")} disabled={busy === "export"} data-testid="v5-export-png" className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-2 text-sm font-bold text-black"><Download size={15} /> PNG</button>
            <button onClick={() => exportImg("jpeg")} data-testid="v5-export-jpeg" className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold">JPEG</button>
            <button onClick={() => exportImg("webp")} data-testid="v5-export-webp" className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold">WebP</button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[260px_1fr_300px]">
          {/* LEFT: inputs */}
          <div className="space-y-3">
            <Panel title="Main Subject (source of truth)">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#00F0FF]/40 p-3 text-xs text-[#00F0FF] hover:bg-[#00F0FF]/5" data-testid="v5-upload-label">
                <Upload size={15} /> {busy === "upload" ? "Uploading…" : "Upload / drop image"}
                <input type="file" accept="image/*" className="hidden" data-testid="v5-upload" onChange={(e) => e.target.files[0] && addImage(e.target.files[0], "subject")} />
              </label>
              <p className="mt-1 text-[11px] text-[#71717A]">Or paste (Ctrl/Cmd+V). The original is never modified.</p>
            </Panel>
            <Panel title="Add layers">
              <div className="grid grid-cols-1 gap-2">
                <button onClick={() => addText(true)} data-testid="v5-add-headline-behind" className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs hover:bg-white/5"><Type size={14} /> Headline BEHIND subject</button>
                <button onClick={() => addText(false)} data-testid="v5-add-text" className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs hover:bg-white/5"><Type size={14} /> Text (front)</button>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs hover:bg-white/5"><ImageIcon size={14} /> Foreground image<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && addImage(e.target.files[0], "foreground")} /></label>
              </div>
            </Panel>
            <Panel title="Canvas">
              <select value={state.canvas.aspect} onChange={(e) => patch({ canvas: { aspect: e.target.value } })} data-testid="v5-aspect" className="w-full rounded-lg border border-white/15 bg-black/40 px-2 py-2 text-xs">
                {Object.keys(ASPECTS).map((a) => <option key={a} value={a}>{`${a} · ${ASPECTS[a][0]}×${ASPECTS[a][1]}`}</option>)}
              </select>
            </Panel>
            <Panel title="Background">
              <select value={state.background.type} onChange={(e) => patch({ background: { ...state.background, type: e.target.value } })} data-testid="v5-bg-type" className="mb-2 w-full rounded-lg border border-white/15 bg-black/40 px-2 py-2 text-xs">
                {["solid", "gradient", "paper", "transparent", "image"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {(state.background.type === "solid" || state.background.type === "gradient") && <input type="color" value={state.background.color} onChange={(e) => patch({ background: { ...state.background, color: e.target.value } })} className="h-8 w-full rounded" />}
              {state.background.type === "gradient" && <input type="color" value={state.background.color2} onChange={(e) => patch({ background: { ...state.background, color2: e.target.value } })} className="mt-1 h-8 w-full rounded" />}
              {state.background.type === "image" && <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-white/15 px-2 py-2 text-xs"><Upload size={13} /> Upload background<input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files[0]; if (f) { const r = await api.igUpload(f, "bg"); patch({ background: { ...state.background, path: r.path } }); } }} /></label>}
            </Panel>
          </div>

          {/* CENTER: canvas */}
          <div className="flex items-start justify-center rounded-xl border border-white/10 bg-[#0D0D0E] p-4">
            <div className="relative" style={{ maxWidth: "100%", maxHeight: "72vh", aspectRatio: `${W}/${H}` }}>
              <canvas ref={canvasRef} width={W} height={H} onMouseDown={onCanvasDown} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp} onMouseLeave={onCanvasUp}
                data-testid="v5-canvas"
                className="h-auto w-full cursor-move rounded-lg shadow-2xl" style={{ background: state.background.type === "transparent" ? "repeating-conic-gradient(#333 0% 25%, #222 0% 50%) 50% / 20px 20px" : "#000" }} />
            </div>
          </div>

          {/* RIGHT: properties */}
          <div className="space-y-3" data-testid="v5-props">
            {!selLayer ? <Panel title="Properties"><p className="text-xs text-[#71717A]">Select a layer to edit its properties. Drag on the canvas to reposition.</p></Panel> : selLayer.type === "text" ? (
              <Panel title="Text">
                <textarea value={selLayer.text} onChange={(e) => patchLayer(sel, { text: e.target.value })} data-testid="v5-text-input" rows={2} className="mb-2 w-full rounded-lg border border-white/15 bg-black/40 px-2 py-1.5 text-xs" />
                <Row><select value={selLayer.font} onChange={(e) => patchLayer(sel, { font: e.target.value })} className="wd5-inp">{FONTS.map((f) => <option key={f}>{f}</option>)}</select><input type="color" value={selLayer.color} onChange={(e) => patchLayer(sel, { color: e.target.value })} className="h-8 w-10 rounded" /></Row>
                <Slider label="Size" v={selLayer.size} min={0.02} max={0.3} step={0.005} on={(v) => patchLayer(sel, { size: v })} />
                <Slider label="Weight" v={selLayer.weight} min={100} max={900} step={100} on={(v) => patchLayer(sel, { weight: v })} />
                <Slider label="Rotation" v={selLayer.rotation} min={-180} max={180} step={1} on={(v) => patchLayer(sel, { rotation: v })} />
                <Slider label="Opacity" v={selLayer.opacity} min={0} max={1} step={0.05} on={(v) => patchLayer(sel, { opacity: v })} />
                <Row><label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!selLayer.uppercase} onChange={(e) => patchLayer(sel, { uppercase: e.target.checked })} /> Upper</label><label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!selLayer.outline} onChange={(e) => patchLayer(sel, { outline: e.target.checked })} /> Outline</label><label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!selLayer.shadow} onChange={(e) => patchLayer(sel, { shadow: e.target.checked })} /> Shadow</label></Row>
                {selLayer.outline && <><Slider label="Outline w" v={selLayer.outlineWidth} min={0} max={20} step={1} on={(v) => patchLayer(sel, { outlineWidth: v })} /><input type="color" value={selLayer.outlineColor} onChange={(e) => patchLayer(sel, { outlineColor: e.target.value })} className="h-7 w-full rounded" /></>}
                <select value={selLayer.align} onChange={(e) => patchLayer(sel, { align: e.target.value })} className="wd5-inp mt-1">{["left", "center", "right"].map((a) => <option key={a}>{a}</option>)}</select>
              </Panel>
            ) : (
              <Panel title="Image / Subject">
                <div className="mb-2 flex gap-2">
                  <button onClick={() => setMaskEdit(sel)} data-testid="v5-mask-open" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#00F0FF]/15 px-2 py-2 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/25"><Scissors size={13} /> Mask / Cut out</button>
                  {selLayer.maskedPath && <button onClick={() => patchLayer(sel, { maskedPath: null })} data-testid="v5-mask-clear" className="rounded-lg border border-white/15 px-2 py-2 text-xs">Revert</button>}
                </div>
                {selLayer.maskedPath && <p className="mb-2 text-[11px] text-[#22c55e]">✓ Masked cut-out active · original kept</p>}
                <Slider label="Scale" v={selLayer.scale} min={0.1} max={3} step={0.02} on={(v) => patchLayer(sel, { scale: v })} />
                <Slider label="Rotation" v={selLayer.rotation} min={-180} max={180} step={1} on={(v) => patchLayer(sel, { rotation: v })} />
                <Slider label="Opacity" v={selLayer.opacity} min={0} max={1} step={0.05} on={(v) => patchLayer(sel, { opacity: v })} />
                <div className="mt-2 text-[11px] font-bold uppercase tracking-wider text-[#71717A]">Effects (deterministic)</div>
                <Slider label="Grayscale" v={selLayer.effects.grayscale || 0} min={0} max={1} step={0.05} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, grayscale: v } })} />
                <Slider label="Sepia" v={selLayer.effects.sepia || 0} min={0} max={1} step={0.05} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, sepia: v } })} />
                <Slider label="Brightness" v={selLayer.effects.brightness ?? 1} min={0.3} max={2} step={0.05} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, brightness: v } })} />
                <Slider label="Contrast" v={selLayer.effects.contrast ?? 1} min={0.3} max={2} step={0.05} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, contrast: v } })} />
                <Slider label="Saturate" v={selLayer.effects.saturate ?? 1} min={0} max={2} step={0.05} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, saturate: v } })} />
                <Slider label="Blur" v={selLayer.effects.blur || 0} min={0} max={20} step={0.5} on={(v) => patchLayer(sel, { effects: { ...selLayer.effects, blur: v } })} />
              </Panel>
            )}
          </div>
        </div>

        {/* BOTTOM: layer stack */}
        <div className="mt-4 rounded-xl border border-white/10 bg-[#0D0D0E] p-3" data-testid="v5-layer-stack">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]"><LayersIcon size={13} /> Layer stack (top = front)</div>
          <div className="flex flex-col-reverse gap-1">
            {state.layers.length === 0 && <div className="py-2 text-center text-xs text-[#71717A]">No layers yet. Upload a subject or add text.</div>}
            {state.layers.map((l) => (
              <div key={l.id} onClick={() => setSel(l.id)} data-testid={`v5-layer-${l.id}`} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${sel === l.id ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-white/10"}`}>
                <span className="truncate">{l.type === "text" ? `T · ${(l.text || "").slice(0, 20)}` : `IMG · ${l.role}`}</span>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); move(l.id, 1); }} className="rounded p-1 hover:bg-white/10"><ArrowUp size={13} /></button>
                  <button onClick={(e) => { e.stopPropagation(); move(l.id, -1); }} className="rounded p-1 hover:bg-white/10"><ArrowDown size={13} /></button>
                  <button onClick={(e) => { e.stopPropagation(); del(l.id); }} className="rounded p-1 hover:bg-white/10"><Trash2 size={13} className="text-[#ef4444]" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {maskEdit && (() => {
        const ml = state.layers.find((l) => l.id === maskEdit);
        if (!ml) return null;
        return <V5MaskEditor originalPath={ml.path} onClose={() => setMaskEdit(null)}
          onApply={({ maskedPath }) => { patchLayer(maskEdit, { maskedPath }); setMaskEdit(null); }} />;
      })()}
      <style>{`.wd5-inp{flex:1;border-radius:.5rem;border:1px solid rgba(255,255,255,.15);background:rgba(0,0,0,.4);padding:.35rem .5rem;font-size:.72rem;color:#fff}`}</style>
    </div>
  );
}

const Panel = ({ title, children }) => <div className="rounded-xl border border-white/10 bg-[#0D0D0E] p-3"><div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">{title}</div>{children}</div>;
const Row = ({ children }) => <div className="mb-1 flex items-center gap-2">{children}</div>;
const Slider = ({ label, v, min, max, step, on }) => (
  <label className="mb-1 block">
    <span className="flex justify-between text-[10px] text-[#71717A]"><span>{label}</span><span>{typeof v === "number" ? v.toFixed?.(2) ?? v : v}</span></span>
    <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => on(parseFloat(e.target.value))} className="w-full accent-[#00F0FF]" />
  </label>
);
